import { ContentObject, ReportSectionContent } from './types';
// import { calculateAverageScore } from './scoreHelper'; // Remove self-import

// Define the score kind type for better IDE completion
export type ScoreKind = ContentObject['scores'][0]['kind'];

// Define a consistent order for scores to ensure they always appear in the same order
export const SCORE_KIND_ORDER: ScoreKind[] = [
    'reliabilityOfSource',
    'relevanceToClient',
    'relevanceToUseCase',
    'detailedMetrics',
    'strategicAndTacticalInformation',
    'technicalImplementationDetails',
    'noVendorBias',
];

// Default values for scores if not provided
export const DEFAULT_SCORE_VALUES: Record<ScoreKind, number> = {
    reliabilityOfSource: 7.0,
    relevanceToClient: 7.0,
    relevanceToUseCase: 7.0,
    detailedMetrics: 5.0,
    strategicAndTacticalInformation: 5.0,
    technicalImplementationDetails: 5.0,
    noVendorBias: 5.0,
};

// Score value texts with descriptions for each score value
const SCORE_TEXTS: Record<ScoreKind, string> = {
    detailedMetrics: 'Complete metrics',
    relevanceToUseCase: 'Highly relevant',
    reliabilityOfSource: 'Official source',
    strategicAndTacticalInformation: 'Strategic insights',
    technicalImplementationDetails: 'Comprehensive detail',
    noVendorBias: 'Unbiased content',
    relevanceToClient: 'Client applicable',
};

// Display names that are shorter and more concise
const SCORE_DISPLAY_NAMES: Record<ScoreKind, string> = {
    detailedMetrics: 'Metrics',
    relevanceToUseCase: 'Use Case Relevance',
    reliabilityOfSource: 'Reliability',
    strategicAndTacticalInformation: 'Strategy/Tactics',
    technicalImplementationDetails: 'Implementation Detail',
    noVendorBias: 'Vendor Bias',
    relevanceToClient: 'Client Relevance',
};

/**
 * Represents a standard score object structure.
 */
interface Score {
    kind: ScoreKind;
    value: number;
}

/**
 * Maps a numeric score value (0-10) to a qualitative level string.
 */
function getScoreLevelText(value: number): string {
    if (value >= 9.5) return 'Excellent';
    if (value >= 8.0) return 'Great';
    if (value > 7.0) return 'Good';
    if (value >= 4.0) return 'Moderate';
    return 'Poor';
}

/**
 * Helper to create standardized score objects from a simple map of kinds to values.
 * Accepts partial scores and uses defaults for missing ones.
 * Returns scores in a consistent, predefined order.
 * Generates dynamic text based on the score value.
 *
 * @param scoreValues Object mapping score kinds to their numeric values (1-10). Can be partial.
 * @returns Array of fully formatted score objects for use in content fixtures, ordered consistently.
 */
export function createScores(scoreValues: Partial<Record<ScoreKind, number>> = {}) {
    // Combine provided values with defaults
    const completeScoreValues: Record<ScoreKind, number> = {
        ...DEFAULT_SCORE_VALUES,
        ...scoreValues,
    };

    // Map to the full score object format
    const scoreObjects = Object.entries(completeScoreValues).map(([kind, value]) => {
        const scoreKind = kind as ScoreKind;
        const validatedValue = Math.max(0, Math.min(10, value)); // Ensure value is 0-10
        const levelText = getScoreLevelText(validatedValue);

        return {
            kind: scoreKind,
            // Generate dynamic text, e.g., "Reliability: High"
            text: `${levelText}`,
            displayName: SCORE_DISPLAY_NAMES[scoreKind],
            value: validatedValue,
        };
    });

    // Sort the scores based on the defined order
    scoreObjects.sort((a, b) => {
        const indexA = SCORE_KIND_ORDER.indexOf(a.kind);
        const indexB = SCORE_KIND_ORDER.indexOf(b.kind);
        return indexA - indexB;
    });

    return scoreObjects;
}

/**
 * Analyzes a set of scores and determines a textual summary classification
 * reflecting the overall quality and consistency.
 *
 * @param scores An array of score objects, each containing a kind and value (0-10).
 * @returns A descriptive summary string reflecting the aggregate score profile.
 */
export function getScoreSummaryText(scores: Score[] | null): string {
    if (!scores || scores.length === 0) {
        return 'Score unavailable';
    }

    const averageScore = calculateAverageScore(scores);
    if (averageScore === null) {
        // Defensive check
        return 'Score calculation error';
    }

    // Define thresholds
    const exceptionalThreshold = 9.5;
    const highThreshold = 8.0;
    const midThreshold = 6.0;
    const lowThreshold = 4.0;

    // Generate summary based *only* on the average score
    if (averageScore >= exceptionalThreshold) {
        return 'Excellent relevance and highly informative';
    }
    if (averageScore >= highThreshold) {
        return 'Highly relevant and informative content';
    }
    if (averageScore >= midThreshold) {
        return 'Generally relevant and informative';
    }
    if (averageScore >= lowThreshold) {
        return 'Moderately relevant or lacks depth/informativeness';
    }

    // Average score is below lowThreshold
    return 'Suggests low relevance or lacks informative value';
}

/**
 * Utility function to calculate the average score from a collection of score objects.
 *
 * @param scores Array of score objects (expects the structure { value: number }).
 * @returns The average score value (0-10), or null if the input array is empty.
 */
export function calculateAverageScore(scores: Array<{ value: number }>): number | null {
    if (!scores || scores.length === 0) {
        return null;
    }

    const sum = scores.reduce((acc, score) => acc + score.value, 0);
    const average = sum / scores.length;

    // Ensure the average is within the 0-10 range and formatted to one decimal place
    return parseFloat(Math.max(0, Math.min(10, average)).toFixed(1));
}

/**
 * Gets the appropriate color scheme based on a score value
 * @param score The score value (0-10)
 * @returns 'red', 'orange', or 'green' based on the score value
 */
export function getColorSchemeForScore(score: number): 'red' | 'orange' | 'green' {
    if (score < 4) {
        return 'red';
    } else if (score > 7) {
        return 'green';
    } else {
        return 'orange';
    }
}

export const calculateAverageSourceScore = (source: ContentObject): number => {
    if (!source || !source.scores || !Array.isArray(source.scores)) {
        return 0;
    }
    return (
        source.scores.reduce(
            (sum: number, score: { value: number }) => sum + score.value,
            0
        ) / source.scores.length
    );
};

export function calculateAverageSectionScore(section: ReportSectionContent): number {
    if (!section || !section.sources || !Array.isArray(section.sources)) {
        return 0;
    }
    return (
        section.sources.reduce(
            (sum: number, source: ContentObject) =>
                sum + calculateAverageSourceScore(source),
            0
        ) / section.sources.length
    );
}
